import { castArray, compact } from 'lodash-es'
import moment from 'moment-timezone'

import { availableSpots } from './availability_service'
import { DATE_FORMAT, MAX_RANGE_BETWEEN_SPOTS } from './constants'
import { unavailabilityForDateRange } from './mutual_availability'
import { calculateEventsOverlap } from './overlap_count'
import { timeRange } from './utils'

const getAvailableSpots = (
  unavailabilityRules,
  targetTimezone,
  { rangeStart },
  timeNotation,
  eventTypeDuration,
  selectedSpots,
  memberEvents,
  fetchedEventsDaysRange,
  selectingSpotsEnabled,
  allowEventsOverlap = true,
  allowSpotsOverlap = true,
  allowInternalEventsOverlap = false
) => {
  if (!rangeStart || !selectingSpotsEnabled) return []

  const currentDate = moment.tz(rangeStart, targetTimezone).startOf('day')
  const endOfRange = currentDate.clone().add(fetchedEventsDaysRange - 1, 'days').endOf('day')

  const availableSpotsArray = []
  const selectedSpotsArray = compact(castArray(selectedSpots))

  while (currentDate.isBefore(endOfRange)) {
    const unavailabilityIntervals = unavailabilityRules[currentDate.format(DATE_FORMAT)] || []

    availableSpotsArray.push(
      ...availableSpots(
        currentDate,
        targetTimezone,
        timeNotation,
        eventTypeDuration,
        selectedSpotsArray.filter(({ start: spotStart }) => currentDate.format(DATE_FORMAT) === spotStart.format(DATE_FORMAT)),
        memberEvents,
        unavailabilityIntervals,
        15,
        allowEventsOverlap,
        allowSpotsOverlap,
        allowInternalEventsOverlap
      )
    )

    currentDate.add(1, 'day')
  }

  return availableSpotsArray
}

const backgroundUnavailableSpot = ({ start, end, timezone, timeNotation }) => ({
  display: 'background',
  title: '',
  start: start.toISOString(),
  end: end.toISOString(),
  extendedProps: {
    background: true,
    selectedSpot: false,
    timeRange: timeRange({ start, end, timezone, timeNotation }),
    disabled: true,
    available: false,
    timezone
  }
})

const hasAllDayEvent = (date, allDayEvents) => (
  allDayEvents.some(({ start }) => start === date)
)

const getBackgroundEvents = (
  unavailabilityRules,
  targetTimezone,
  { rangeStart },
  timeNotation,
  selectedSpots,
  fetchedEventsDaysRange,
  reserveTimes,
  selectingSpotsEnabled,
  calendarEvents
) => {
  if (!rangeStart) return []

  const allDayEvents = calendarEvents.filter(({ allDay }) => allDay)
  const currentDate = moment.tz(rangeStart, targetTimezone).startOf('day')
  const endOfRange = currentDate.clone().add(fetchedEventsDaysRange - 1, 'days').endOf('day')

  const backgroundSpots = []

  while (currentDate.isBefore(endOfRange)) {
    const formattedDate = currentDate.format(DATE_FORMAT)
    const unavailabilityIntervals = selectingSpotsEnabled && !hasAllDayEvent(formattedDate, allDayEvents)
      ? unavailabilityRules[formattedDate] || []
      : [{ start: currentDate.clone().startOf('day'), end: currentDate.clone().endOf('day') }]

    unavailabilityIntervals.forEach(({ start, end }) => {
      const spot = backgroundUnavailableSpot({ start, end, timezone: targetTimezone, timeNotation })
      backgroundSpots.push(spot)
    })

    currentDate.add(1, 'day')
  }

  selectedSpots.forEach(({ start, end, invitees, groupSpot, doubleBookingEvents, display = 'background' }) => {
    backgroundSpots.push({
      title: '',
      start: start.toISOString(),
      end: end.toISOString(),
      editable: groupSpot,
      overlap: groupSpot,
      display,
      extendedProps: {
        background: true,
        selectedSpot: true,
        timeRange: timeRange({ start, end, timezone: targetTimezone, timeNotation }),
        available: true,
        timezone: targetTimezone,
        tooltipOpen: false,
        invitees,
        reserveTimes,
        doubleBookingEvents,
        groupSpot
      }
    })
  })

  return backgroundSpots
}

const getCalendarEvents = (events, timezone, timeNotation, currentTime) => (
  calculateEventsOverlap(events).map(({
    title, start, end, allDay, hostIndex, hostType, colorIndex, color, kind, isCalendlyEvent, userInitials, overlapCount
  }) => ({
    start: allDay ? moment.utc(start).format(DATE_FORMAT) : start,
    end: allDay ? moment.utc(end).format(DATE_FORMAT) : end,
    allDay,
    editable: false,
    extendedProps: {
      title,
      hostIndex,
      hostType,
      colorIndex,
      color,
      kind,
      userInitials,
      overlapCount,
      timezone,
      isCalendlyEvent,
      isPast: moment(end).isBefore(currentTime),
      duration: moment(end).diff(moment(start), 'minutes'),
      timeRange: timeRange({ start, end, timezone, timeNotation }),
      background: false
    }
  }))
)

const getCurrentTimeEvent = (currentTime) => {
  const now = currentTime.toISOString()

  return {
    title: '',
    display: 'background',
    overlap: true,
    start: now,
    end: now,
    classNames: ['customNowIndicator'],
    extendedProps: {
      isCurrentTime: true
    }
  }
}

const timeToDate = (time, currentDate) => {
  const date = currentDate.clone()

  date.hour(time.split(':')[0])
  date.minutes(time.split(':')[1])

  return date
}

const getUnavailabilityIntervals = (schedules, targetTimezone, { rangeStart }, fetchedEventsDaysRange) => {
  if (schedules.length === 0) return {}

  const currentDate = moment.tz(rangeStart, targetTimezone).startOf('day')
  const endOfRange = currentDate.clone().add(fetchedEventsDaysRange - 1, 'days').endOf('day')

  const unavailabilityRules = unavailabilityForDateRange(schedules, targetTimezone, currentDate, endOfRange)

  while (currentDate.isBefore(endOfRange)) {
    const unavailabilityIntervals = unavailabilityRules[currentDate.format(DATE_FORMAT)] || []

    unavailabilityIntervals.forEach((interval, index) => {
      const start = timeToDate(interval.from, currentDate)
      const end = timeToDate(interval.to, currentDate)

      unavailabilityRules[currentDate.format(DATE_FORMAT)][index] = { start, end }
    })

    currentDate.add(1, 'day')
  }

  return unavailabilityRules
}

const spotRangeExceedsLimit = (orderedSpots) => {
  const firstSpotStart = orderedSpots[0].start
  const lastSpotStart = orderedSpots[orderedSpots.length - 1].start

  const durationBetweenSpots = lastSpotStart.diff(firstSpotStart, 'months', true)

  return durationBetweenSpots > MAX_RANGE_BETWEEN_SPOTS
}

export {
  getAvailableSpots,
  getBackgroundEvents,
  getCalendarEvents,
  getCurrentTimeEvent,
  getUnavailabilityIntervals,
  spotRangeExceedsLimit,
}
